import { isDefined } from "@clipboard-health/util-ts";
import { ExternalLink, Span, Text, useModalState } from "@src/lib/ui-react";
import { Card } from "antd";
import { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { fetchAgentPaymentsBalanceInfo } from "../agents/api";
import { logError } from "src/appV2/lib/analytics";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { Divider, Link, Skeleton, Stack, Tooltip } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import { useGetWorkerPayoutSettings } from "../agents/hooks/useGetWorkerPayoutSettings";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { environmentConfig, NodeEnvironment } from "@src/appV2/environment";
import { useResyncPaymentStatusWithStripe } from "../agents/hooks/useResyncPaymentStatusWithStripe";
import { Agent } from "@src/interface";
import { ConfirmationModal } from "@src/appV2/components/ConfirmationModal";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";

const getStripeAccountLink = (accountId: string) => {
  const baseAccountId = "acct_1HrTYbCZ2LZg728W";
  return environmentConfig.REACT_APP_ENVIRONMENT_NAME === NodeEnvironment.PRODUCTION
    ? `https://dashboard.stripe.com/${baseAccountId}/connect/accounts/${accountId}/activity`
    : `https://dashboard.stripe.com/${baseAccountId}/test/connect/accounts/${accountId}/activity`;
};

interface AgentPaymentsInfoCardProps {
  agent: Agent;
  refetchUser: () => void;
}

function AgentPaymentsInfoCard({ agent, refetchUser }: AgentPaymentsInfoCardProps) {
  const { adminProfile } = useSession();
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [balance, setBalance] = useState<any>({
    availableNow: 0,
    availableSoon: 0,
  });
  const agentHasAccountEnabled = agent.paymentAccountInfo?.enabled;

  const workerId = agent.userId ?? "";

  const hasPaymentAccountInfo = isDefined(agent.paymentAccountInfo);
  const {
    data: payoutSettings,
    isLoading: isLoadingPayoutSettings,
    isError: isErrorPayoutSettings,
    error: payoutSettingsError,
  } = useGetWorkerPayoutSettings(workerId, { enabled: hasPaymentAccountInfo });

  const confirmResyncFromStripeModalState = useModalState();
  const {
    mutate: resyncPaymentStatusWithStripe,
    isLoading: isLoadingResyncPaymentStatusWithStripe,
  } = useResyncPaymentStatusWithStripe({
    onSuccess: () => {
      showSuccessToast("Payment status synced with Stripe");
      refetchUser();
    },
    // oxlint-disable-next-line typescript/restrict-template-expressions
    onError: (error) => showErrorToast(`Failed to resync payment status with Stripe: ${error}`),
  });

  useEffect(() => {
    async function loadBalance() {
      try {
        if (!agent || !agentHasAccountEnabled) {
          return;
        }
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const balance = await fetchAgentPaymentsBalanceInfo({
          userId: workerId,
        });
        setBalance(balance);
      } catch (error: unknown) {
        logError("Error in AgentPaymentsInfoCard", {
          error,
        });
        showErrorToast(
          "Error occurred while validating the user's stripe balance, please try again"
        );
      }
    }

    void loadBalance();
  }, [agent, agentHasAccountEnabled, workerId]);

  const titleText = useMemo(() => {
    if (!agentHasAccountEnabled) {
      return "Not Yet Stripe Enabled";
    }

    return `Stripe enabled on ${moment(agent.paymentAccountInfo?.enabledAt).format("MM/DD/YYYY")}`;
  }, [agent, agentHasAccountEnabled]);

  const stripeAccountLink = useMemo(() => {
    if (!agent.paymentAccountInfo?.accountId) {
      return undefined;
    }
    return getStripeAccountLink(agent.paymentAccountInfo.accountId);
  }, [agent.paymentAccountInfo?.accountId]);

  const isStatusProblem = useMemo(() => {
    if (!agentHasAccountEnabled) {
      return true;
    }
    const status = agent.paymentAccountInfo?.status;
    return (
      status === "Account Needs Updating" ||
      status === "Standard Payouts Pending Approval" ||
      status === "Instant Payouts Pending Approval" ||
      status === "Account Created"
    );
  }, [agent, agentHasAccountEnabled]);

  return (
    <Card title={`Worker Pay Information — ${titleText}`}>
      {!hasPaymentAccountInfo && (
        <Text variant="body1" italic sx={(theme) => ({ color: theme.palette.warning.main })}>
          This HCP did not go through the Stripe onboarding process.
        </Text>
      )}
      {isLoadingPayoutSettings && <Skeleton variant="text" width="100%" height={60} />}
      {isErrorPayoutSettings && (
        <Text variant="body1" italic sx={(theme) => ({ color: theme.palette.warning.main })}>
          {payoutSettingsError?.message.includes("404")
            ? "This HCP did not go through the Stripe onboarding process."
            : "Cannot load payout settings: " + payoutSettingsError?.message}
        </Text>
      )}
      {payoutSettings && (
        <Stack direction="column" spacing={1}>
          {/* Actual Worker Schedule */}
          {payoutSettings.data.attributes.payoutSchedule === "instant" &&
            payoutSettings.data.meta.payoutCapability !== "none" && (
              <Text
                variant="body1"
                sx={(theme) => ({
                  color: theme.palette.success.main,
                })}
              >
                <BoltIcon sx={{ verticalAlign: "middle", marginRight: 0.5 }} />
                Worker is set to receive payouts <strong>Instantly</strong>.
              </Text>
            )}
          {payoutSettings.data.attributes.payoutSchedule === "standard" &&
            payoutSettings.data.meta.payoutCapability !== "none" && (
              <Text
                variant="body1"
                sx={(theme) => ({
                  color: theme.palette.info.main,
                })}
              >
                <AccountBalanceWalletIcon sx={{ verticalAlign: "middle", marginRight: 0.5 }} />
                Worker is set to receive payouts <strong>Standard</strong>.
              </Text>
            )}
          {payoutSettings.data.meta.payoutCapability === "none" && (
            <Text
              variant="body1"
              sx={(theme) => ({
                color: theme.palette.error.main,
              })}
            >
              <RemoveCircleOutlineIcon sx={{ verticalAlign: "middle", marginRight: 0.5 }} />
              Worker's Stripe account cannot receive payouts. CBH schedule is configured as{" "}
              {payoutSettings.data.attributes.payoutSchedule === "instant"
                ? "InstantPay"
                : "StandardPay"}
            </Text>
          )}

          <Divider />

          {/* Payout Destination */}
          <Text variant="body1">
            Payout Destination:{" "}
            <strong>
              {payoutSettings.data.attributes.preferredExternalAccountStripeId ?? "N/A"}
            </strong>
          </Text>

          {/* Instant Pay Eligibility - only show if on Standard AND has instant capability */}
          {payoutSettings.data.attributes.payoutSchedule === "standard" &&
            payoutSettings.data.meta.payoutCapability === "instant" && (
              <Tooltip title="The user can change their payout destination in Stripe to receive InstantPay.">
                <Text variant="body1">
                  Worker has an <strong>InstantPay</strong> eligible payout method available.
                </Text>
              </Tooltip>
            )}

          {/* Standard Pay Eligibility - only show if on Instant AND has standard capability */}
          {payoutSettings.data.attributes.payoutSchedule === "instant" &&
            payoutSettings.data.meta.payoutCapability === "standard" && (
              <Tooltip title="The user should change their payout settings in the CBH app to receive StandardPay.">
                <Text variant="body1" sx={(theme) => ({ color: theme.palette.warning.main })}>
                  Worker does not have any InstantPay eligible payout method, but has{" "}
                  <strong>InstantPay</strong> payout schedule set in the CBH app.
                </Text>
              </Tooltip>
            )}

          <Divider />

          {/* Stripe Account Link */}
          {agent.paymentAccountInfo?.accountId && stripeAccountLink && (
            <Text variant="body1">
              Stripe Account: {agent.paymentAccountInfo.accountId}{" "}
              <ExternalLink to={stripeAccountLink} aria-label="View Stripe account details">
                <ArrowForwardIcon
                  sx={{ fontSize: 16, marginTop: -0.25, verticalAlign: "middle" }}
                />
              </ExternalLink>
            </Text>
          )}

          {/* Payment Account Status */}
          <Text
            variant="body1"
            sx={(theme) => ({
              color: isStatusProblem ? theme.palette.error.main : theme.palette.text.primary,
            })}
          >
            Payment Account Status: <strong>{agent.paymentAccountInfo?.status ?? "N/A"}</strong>
            {!agentHasAccountEnabled && agent.paymentAccountInfo?.disabledReason && (
              <> — {agent.paymentAccountInfo.disabledReason}</>
            )}{" "}
            {(adminProfile?.roles?.includes(EMPLOYEE_ROLES.ADMINISTRATIVE) ||
              adminProfile?.roles?.includes(EMPLOYEE_ROLES.PAYROLL)) &&
              (isLoadingResyncPaymentStatusWithStripe ? (
                "..."
              ) : (
                <Link onClick={confirmResyncFromStripeModalState.openModal}>
                  <RefreshIcon sx={{ fontSize: 16, marginTop: -0.25, verticalAlign: "middle" }} />
                </Link>
              ))}
          </Text>

          <Divider />

          <Text variant="body1">
            Available to Withdraw:{" "}
            <Span sx={{ fontWeight: balance.availableNow > 0 ? "bold" : "normal" }}>
              {agentHasAccountEnabled ? formatDollarsAsUsd(balance.availableNow / 100) : "N/A"}
            </Span>
          </Text>

          {/* Payouts In Transit */}
          <Text variant="body1">
            Payouts In Transit:{" "}
            {agentHasAccountEnabled ? formatDollarsAsUsd(balance.availableSoon / 100) : "N/A"}
          </Text>
        </Stack>
      )}
      <ConfirmationModal
        modalState={confirmResyncFromStripeModalState}
        title="Resync Payment Status from Stripe"
        message="This will fetch information from Stripe and update the worker's payment account status. Are you sure you want to continue?"
        onConfirm={() => resyncPaymentStatusWithStripe({ workerId })}
      />
    </Card>
  );
}

export { AgentPaymentsInfoCard };
